/**
 * One station on the chef board: its coloured title bar and its queue below,
 * filling the whole height of the screen.
 *
 * There is no empty state. A station with nothing in it is not drawn at all —
 * the board decides that (`boardColumns`), and drawing it here would put back
 * exactly the empty panel this feature removed.
 *
 * The scrolling and its two buttons come from `ScrollPane`, which puts them in
 * rails above and below the orders rather than floating them over the first
 * line of the next one — and draws no rail at all when everything fits.
 */
import { ChevronLeft, ChevronRight } from "lucide-react";
import OrderCard from "./OrderCard";
import ScrollPane from "./ScrollPane";
import { stationHasWork } from "../lib/station-layout";
import { useTranslation } from "../i18n";

interface StationColumnProps {
  station: any;
  orders: any[];
  canMoveLeft?: boolean;
  canMoveRight?: boolean;
  onMoveLeft?: () => void;
  onMoveRight?: () => void;
}

/** Orders with something this station still has to cook. */
function ordersForStation(orders: any[], stationId: string): any[] {
  return orders.filter((o) => stationHasWork([o], stationId));
}

export default function StationColumn({
  station,
  orders,
  canMoveLeft,
  canMoveRight,
  onMoveLeft,
  onMoveRight,
}: StationColumnProps) {
  const { t } = useTranslation();
  const stationOrders = ordersForStation(orders, station.id);

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      <div
        className="flex items-center justify-between px-4 py-2 flex-shrink-0 rounded-t-xl"
        style={{ backgroundColor: station.color || "#4361EE" }}
      >
        {onMoveLeft && (
          <button
            onClick={onMoveLeft}
            disabled={!canMoveLeft}
            className="p-2 rounded hover:bg-white/20 transition-colors disabled:opacity-20 min-h-touch min-w-touch"
            title={t("kds.station.moveLeft", "Sposta a sinistra")}
          >
            <ChevronLeft size={22} className="text-white" />
          </button>
        )}
        <div className="flex items-center gap-3 flex-1 justify-center min-w-0">
          <h2 className="text-kds-station text-white truncate">{station.name}</h2>
          <span className="text-kds-meta px-3 py-0.5 rounded-full shrink-0 bg-white/20 text-white">
            {stationOrders.length}
          </span>
        </div>
        {onMoveRight && (
          <button
            onClick={onMoveRight}
            disabled={!canMoveRight}
            className="p-2 rounded hover:bg-white/20 transition-colors disabled:opacity-20 min-h-touch min-w-touch"
            title={t("kds.station.moveRight", "Sposta a destra")}
          >
            <ChevronRight size={22} className="text-white" />
          </button>
        )}
      </div>
      <ScrollPane
        className="bg-kds-bg/50 rounded-b-xl"
        bodyClassName="p-3 space-y-4"
        revision={stationOrders.length}
        labels={{ up: t("kds.station.ordersAbove", "Comande sopra"), down: t("kds.station.moreOrders", "Altre comande") }}
      >
        {stationOrders.map((order) => (
          <OrderCard key={order.id} order={order} compact stationId={station.id} />
        ))}
      </ScrollPane>
    </div>
  );
}
